/**
 * Pure geometry helpers for GroundTrackMapLeaflet.
 * Used for antimeridian splitting, geodesic circles, and world copies.
 * Longitudes are normalized to [-180, 180] where required.
 */

/** Normalize longitude to [-180, 180]. */
export function wrapLon(lon: number): number {
  let l = lon;
  while (l > 180) l -= 360;
  while (l < -180) l += 360;
  return l;
}

/** Normalize an array of [lat, lon] so all longitudes are in [-180, 180]. */
export function normalizeLatLngs(
  points: [number, number][],
): [number, number][] {
  return points.map(([lat, lon]) => [lat, wrapLon(lon)] as [number, number]);
}

/**
 * Split a polyline at the antimeridian (±180°).
 * Input points should be normalized (wrapLon) so crossing is detected correctly.
 * No segment will contain consecutive points with |dLon| > 180 (avoids vertical line at date line).
 */
export function splitPolylineAtAntimeridian(
  points: [number, number][],
): [number, number][][] {
  if (points.length < 2) return points.length ? [points] : [];
  const normalized = normalizeLatLngs(points);
  const segments: [number, number][][] = [];
  let current: [number, number][] = [[...normalized[0]]];
  for (let i = 1; i < normalized.length; i++) {
    const prev = normalized[i - 1];
    const curr: [number, number] = [...normalized[i]];
    const dLon = curr[1] - prev[1];
    if (Math.abs(dLon) > 180) {
      const edgeLon = dLon > 0 ? -180 : 180;
      // Unwrap curr to get correct interpolation across the dateline
      const unwrappedCurr = dLon > 0 ? curr[1] - 360 : curr[1] + 360;
      const t = (edgeLon - prev[1]) / (unwrappedCurr - prev[1]);
      const latEdge = prev[0] + t * (curr[0] - prev[0]);
      current.push([latEdge, edgeLon]);
      segments.push(current);
      const otherEdge = edgeLon === 180 ? -180 : 180;
      current = [[latEdge, otherEdge], curr];
    } else {
      current.push(curr);
    }
  }
  segments.push(current);
  return segments;
}

/** Return each segment plus world copies at lon ± 360. */
export function segmentsWithWorldCopies(
  segments: [number, number][][],
): [number, number][][] {
  const out: [number, number][][] = [];
  for (const seg of segments) {
    out.push(seg);
    out.push(seg.map(([lat, lon]) => [lat, lon + 360] as [number, number]));
    out.push(seg.map(([lat, lon]) => [lat, lon - 360] as [number, number]));
  }
  return out;
}

/** Geodesic circle as lat/lon polygon (for coverage that looks correct at all latitudes). */
export function geodesicCirclePoints(
  centerLat: number,
  centerLon: number,
  radiusDeg: number,
  numPoints: number = 64,
): [number, number][] {
  const lat0 = (centerLat * Math.PI) / 180;
  const lon0 = (centerLon * Math.PI) / 180;
  const d = (radiusDeg * Math.PI) / 180;
  const points: [number, number][] = [];
  for (let i = 0; i <= numPoints; i++) {
    const bearing = (i / numPoints) * 2 * Math.PI;
    const sinLat =
      Math.sin(lat0) * Math.cos(d) +
      Math.cos(lat0) * Math.sin(d) * Math.cos(bearing);
    const lat = Math.asin(Math.max(-1, Math.min(1, sinLat)));
    const lon =
      lon0 +
      Math.atan2(
        Math.sin(bearing) * Math.sin(d) * Math.cos(lat0),
        Math.cos(d) - Math.sin(lat0) * sinLat,
      );
    let lonDeg = (lon * 180) / Math.PI;
    lonDeg = wrapLon(lonDeg);
    points.push([(lat * 180) / Math.PI, lonDeg]);
  }
  return points;
}

/**
 * Geodesic circle with continuous (unwrapped) longitudes.
 * All output longitudes stay within ±180° of centerLon, producing a single
 * clean polygon that never jumps across the antimeridian.
 * Leaflet renders out-of-range longitudes correctly, so no splitting is needed.
 */
export function geodesicCirclePointsContinuous(
  centerLat: number,
  centerLon: number,
  radiusDeg: number,
  numPoints: number = 64,
): [number, number][] {
  const lat0 = (centerLat * Math.PI) / 180;
  const lon0 = (centerLon * Math.PI) / 180;
  const d = (radiusDeg * Math.PI) / 180;
  const points: [number, number][] = [];
  for (let i = 0; i <= numPoints; i++) {
    const bearing = (i / numPoints) * 2 * Math.PI;
    const sinLat =
      Math.sin(lat0) * Math.cos(d) +
      Math.cos(lat0) * Math.sin(d) * Math.cos(bearing);
    const lat = Math.asin(Math.max(-1, Math.min(1, sinLat)));
    const lon =
      lon0 +
      Math.atan2(
        Math.sin(bearing) * Math.sin(d) * Math.cos(lat0),
        Math.cos(d) - Math.sin(lat0) * sinLat,
      );
    let lonDeg = (lon * 180) / Math.PI;
    while (lonDeg - centerLon > 180) lonDeg -= 360;
    while (lonDeg - centerLon < -180) lonDeg += 360;
    points.push([(lat * 180) / Math.PI, lonDeg]);
  }
  return points;
}

/**
 * Split a closed ring (e.g. geodesic circle) at ±180° so each part can be drawn without wrapping.
 * Returns one ring if the circle does not cross the date line, or two closed rings (east/west) if it does.
 */
export function splitRingAtAntimeridian(
  ring: [number, number][],
): [number, number][][] {
  if (ring.length < 3) return ring.length ? [ring] : [];
  const pts = ring.map(([lat, lon]) => [lat, wrapLon(lon)] as [number, number]);
  const segments: [number, number][][] = [];
  let current: [number, number][] = [pts[0]];
  let latAt180: number | null = null;
  let latAtNeg180: number | null = null;

  for (let i = 1; i <= pts.length; i++) {
    const prev = pts[i - 1];
    const curr = i < pts.length ? pts[i] : pts[0];
    const dLon = curr[1] - prev[1];
    if (Math.abs(dLon) > 180) {
      // Must match splitPolylineAtAntimeridian: dLon > 0 → crossing from west to east → exit at -180
      const edgeLon = dLon > 0 ? -180 : 180;
      // Unwrap curr to get correct interpolation across the dateline
      const unwrappedCurr = dLon > 0 ? curr[1] - 360 : curr[1] + 360;
      const t = (edgeLon - prev[1]) / (unwrappedCurr - prev[1]);
      const latEdge = prev[0] + t * (curr[0] - prev[0]);
      current.push([latEdge, edgeLon]);
      segments.push(current);
      if (edgeLon === 180) latAt180 = latEdge;
      else latAtNeg180 = latEdge;
      const otherEdge = edgeLon === 180 ? -180 : 180;
      current = [[latEdge, otherEdge]];
    } else {
      if (i < pts.length) current.push(curr);
    }
  }
  if (current.length > 0) segments.push(current);

  if (segments.length <= 1) return segments.length ? [segments[0]] : [ring];

  const lat1 = latAt180 ?? latAtNeg180 ?? 0;
  const lat2 = latAtNeg180 ?? latAt180 ?? 0;
  const rings: [number, number][][] = [];
  for (const seg of segments) {
    const lastLon = seg[seg.length - 1][1];
    if (lastLon === 180) rings.push([...seg, [lat2, 180]]);
    else rings.push([...seg, [lat1, -180]]);
  }
  return rings;
}
